import React from "react";
import { MapPin, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { usePageMeta } from "@/components/site/usePageMeta";
import { PageHero } from "@/components/site/PageHero";
import { SectionHeading } from "@/components/site/ui";
import { Reveal } from "@/components/site/useReveal";
import { ServiceAreas } from "@/components/site/ServiceAreas";
import { FeaturedCities } from "@/components/site/FeaturedCities";
import { ContactSection } from "@/components/site/ContactSection";
import { ROUTES } from "@/components/site/data";

const IMG = "https://images.unsplash.com/photo-1768348256583-129b0cc812c2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

// Communities we actively serve, grouped by county. Every card links into
// the live IDX search so visitors immediately see real homes.
const LA_COUNTY = [
  "Los Angeles", "Long Beach", "Cerritos", "Lakewood", "Downey", "Whittier",
  "Norwalk", "Bellflower", "San Gabriel", "Torrance", "Glendale", "Pasadena",
];
const OC_COUNTY = [
  "Anaheim", "Costa Mesa", "Irvine", "Fullerton", "Buena Park", "Huntington Beach",
  "Santa Ana", "Orange", "Garden Grove", "Cypress", "La Palma", "Newport Beach",
];

const CityChip = ({ name }) => (
  <Link
    to={ROUTES.idx}
    data-testid={`areas-city-${name.toLowerCase().replace(/\s+/g, "-")}`}
    className="group flex items-center justify-between bg-white border border-[#E5E5E5] rounded-xl px-4 py-3 hover:border-[#C9A961] transition-colors"
  >
    <span className="flex items-center gap-2 text-[15px] font-medium text-[#2D2D37]">
      <MapPin size={15} className="text-[#254369]" /> {name}
    </span>
    <ArrowUpRight size={16} className="text-[#2D2D37]/30 group-hover:text-[#C9A961] transition-colors" />
  </Link>
);

export default function AreasPage() {
  usePageMeta(
    "Areas We Serve | Wallstreet Realty",
        "Wallstreet Realty serves buyers, sellers, and agents across Los Angeles County, Orange County, and Southern California, including Long Beach, Cerritos, Anaheim, Irvine, and more."
  );
  return (
    <>
      <PageHero
        crumb="Areas"
        eyebrow="Where We Serve"
        title="Local Expertise Across Southern California"
        subtitle="From the coast to the inland communities, Wallstreet Realty helps buyers and sellers across Los Angeles County, Orange County, and the neighborhoods in between."
        image={IMG}
        ctas={[
          { label: "Search Homes by Area", href: ROUTES.idx, testid: "areas-hero-search" , pill: true},
          { label: "Talk to a Local Agent", href: ROUTES.contact, variant: "gold", testid: "areas-hero-contact" },
        ]}
      />

      <section className="py-16 lg:py-24 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Communities"
            title="Cities We Know Street by Street"
                        subtitle="Pick a city to jump into the live home search, or call us and we'll tell you what's really happening in that market this month."
            center
          />
          <div className="mt-12 grid lg:grid-cols-2 gap-10">
            <Reveal>
              <div>
                <h3 className="font-heading font-bold text-xl text-[#2D2D37] mb-4">Los Angeles County</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  {LA_COUNTY.map((c) => <CityChip key={c} name={c} />)}
                </div>
              </div>
            </Reveal>
            <Reveal delay={80}>
              <div>
                <h3 className="font-heading font-bold text-xl text-[#2D2D37] mb-4">Orange County</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  {OC_COUNTY.map((c) => <CityChip key={c} name={c} />)}
                </div>
              </div>
            </Reveal>
          </div>
          <p className="mt-10 text-center text-sm text-[#2D2D37]/55">
                        Don't see your city? We serve all of Southern California,{" "}
            <Link to={ROUTES.contact} className="text-[#254369] font-medium hover:text-[#C9A961]">reach out</Link>{" "}
            and we'll connect you with the right local expert.
          </p>
        </div>
      </section>

      <FeaturedCities />
      <ServiceAreas />
      <ContactSection />
    </>
  );
}
